import { Button } from 'primereact/button';
import { useState, useRef, useEffect } from 'react';
import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { discardSessionRequest, downloadXlsx, generateSessionId, submitFileJob } from '../utils/pdfUtils';

export const PdfFolderSharingForm = () => {
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => { document.title = 'OptiConfig PDF Folder Sharing'; }, []);

  const toast = useRef(null);

  const showError = (err) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: err, life: 3000 });
  };

  const showSuccess = (msg) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
  };

  const [token, setToken] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId());
  const [succeeded, setSucceeded] = useState(false);

  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressDialogVisible, setProgressDialogVisible] = useState(false);
  const [errorFiles, setErrorFiles] = useState([]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const accessToken = await fetchAccessToken(getAccessTokenSilently);
        setToken(accessToken);
      } catch (error) {
        console.error('Error fetching access token:', error);
        showError('Failed to fetch access token. Please try again.');
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!uploadedFiles || uploadedFiles.length === 0) {
      showError('Please upload a .zip file.');
      return;
    }

    setSubmitting(true);

    const fileNames = uploadedFiles.map((file) => file.name);

    try {
      setProgress({ processed: 0, total: fileNames.length });
      setErrorFiles([]);
      setIsProcessing(true);
      setProgressDialogVisible(true);

      try {
        await submitFileJob('/pdf/share-folder', sessionId, token, fileNames);
      } catch (err) {
        setErrorFiles(fileNames);
        throw err;
      }

      setProgress({ processed: fileNames.length, total: fileNames.length });
      setSucceeded(true);
      showSuccess('Folders processed successfully');
    } catch (error) {
      showError(error.message || 'Submission failed.');
    } finally {
      setIsProcessing(false);
      setProgressDialogVisible(false);
      setSubmitting(false);
    }
  };

  const discardSession = async (e) => {
    e?.preventDefault();
    try {
      await discardSessionRequest(sessionId, token);
    } catch (err) {
      showError(err.message || 'Failed to discard session on server.');
    } finally {
      setUploadedFiles([]);
      setSucceeded(false);
      setSessionId(generateSessionId());
      setProgress({ processed: 0, total: 0 });
      setErrorFiles([]);
      setIsProcessing(false);
      setProgressDialogVisible(false);
    }
  };

  const onDownload = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      await downloadXlsx(sessionId, token);
      showSuccess('File downloaded successfully');
      await discardSession();
    } catch (error) {
      showError(error.message || 'Download failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }} onSubmit={onSubmit}>
      <Toast ref={toast} />

      <Dialog
        header="Sharing Progress"
        visible={progressDialogVisible}
        style={{ width: '75vw', maxWidth: '1200px' }}
        onHide={() => setProgressDialogVisible(false)}
        closable={!isProcessing}
        dismissableMask={!isProcessing}
        draggable={false}
        resizable={false}
        modal
      >
        <div className="p-fluid" style={{ fontSize: '0.8rem', lineHeight: 1.25 }}>
          <div className="field">
            <strong>Progress:</strong>
            <div style={{ marginTop: 8 }}>
              <ProgressBar
                value={progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}
              />
              <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                {isProcessing
                  ? 'Processing folders...'
                  : progress.total > 0
                  ? `Processed ${progress.processed} / ${progress.total} files`
                  : 'Idle'}
              </div>
            </div>
          </div>
          {errorFiles.length > 0 && (
            <div className="field">
              <strong>Errors:</strong>
              <ul
                style={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  paddingLeft: 16,
                  fontSize: '0.75rem',
                  color: '#b00020',
                }}
              >
                {errorFiles.map((fname, idx) => (
                  <li key={`err-${fname}-${idx}`}>{fname}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>
      <div className="col-12">
        <div className="grid formgrid p-fluid">
          <div className="field col-12">
            <h4 style={{ textAlign: 'center' }}>PDF Folder Sharing Tool</h4>
          </div>
          <div className="field col-4"></div>
          <div className="field col-4" style={{ display: 'flex', justifyContent: 'center' }}>
            {!succeeded && (
              <Button
                type="submit"
                label={submitting || isProcessing ? 'Submitting...' : 'Submit'}
                className="p-button-primary"
                style={{ maxWidth: '300px', width: '100%' }}
                disabled={uploadedFiles.length === 0 || submitting || isProcessing}
              />
            )}
            {succeeded && (
              <>
                <Button
                  onClick={onDownload}
                  label={submitting ? 'Downloading...' : 'Download .xlsx'}
                  className="p-button-primary"
                  style={{ width: '50%' }}
                  disabled={uploadedFiles.length === 0 || submitting || isProcessing}
                />
              </>
            )}
            {uploadedFiles.length > 0 && (
              <Button
                onClick={discardSession}
                label={'Discard'}
                className="p-button-secondary"
                style={{ width: '50%', marginLeft: 20 }}
                disabled={submitting || isProcessing}
              />
            )}
          </div>
          <div className="field col-4"></div>
          <div className="field col-3"></div>
          <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
            <CustomFileUpload
              allowMultiple={false}
              acceptedFileTypes={['application/zip', 'application/x-zip-compressed', '.zip']}
              placeholderText="Drag & Drop your .zip file or <span class='filepond--label-action'> Browse </span>"
              onMessage={(msg) => {
                showError(msg.detail);
              }}
              token={token}
              fileValue={uploadedFiles}
              sessionId={sessionId}
              onChangeFiles={(files) => {
                setSucceeded(false);
                setUploadedFiles(files);
              }}
            />
          </div>
        </div>
      </div>
    </form>
  );
};
